import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Wallet, Check, Plus, TrendingDown, AlertCircle, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SiteHeader from './SiteHeader';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const COUNTRIES = [['GB', 'United Kingdom'], ['US', 'United States']];

const money = (v, ccy) => (v == null ? '—' : `${ccy === 'USD' ? '$' : '£'}${v.toFixed(2)}`);
const monthLabel = (key) => new Date(key + '-01').toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

export default function Optimiser() {
  const { isAuthenticated, token } = useAuth();
  const [country, setCountry] = useState('GB');
  const [services, setServices] = useState([]);
  const [subs, setSubs] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [schedule, setSchedule] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [plan, setPlan] = useState(null);
  const [planBusy, setPlanBusy] = useState(false);
  const [competitions, setCompetitions] = useState([]);
  const [followed, setFollowed] = useState([]);

  // The sports config is keyed by region (UK/US/World); the optimiser by country.
  const region = country === 'US' ? 'US' : 'UK';

  const authHeaders = useCallback(
    () => ({ 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }),
    [token]
  );

  const loadServices = useCallback(async () => {
    const r = await fetch(`${API_BASE}/api/optimiser/services?country=${country}`, { headers: authHeaders() });
    if (r.ok) setServices((await r.json()).services || []);
  }, [country, authHeaders]);

  const loadSchedule = useCallback(async () => {
    if (!isAuthenticated) return;
    const r = await fetch(`${API_BASE}/api/optimiser/schedule`, { headers: authHeaders() });
    if (r.ok) setSchedule((await r.json()).schedule || []);
    const w = await fetch(`${API_BASE}/watchlist`, { headers: authHeaders() });
    if (w.ok) setWatchlist((await w.json()).watchlist || []);
  }, [isAuthenticated, authHeaders]);

  const loadSubsAndAnalysis = useCallback(async () => {
    if (!isAuthenticated) return;
    const s = await fetch(`${API_BASE}/api/optimiser/subscriptions`, { headers: authHeaders() });
    if (s.ok) setSubs((await s.json()).subscriptions || []);
    const a = await fetch(`${API_BASE}/api/optimiser/analyse?country=${country}`, { headers: authHeaders() });
    if (a.ok) setAnalysis(await a.json());
  }, [isAuthenticated, country, authHeaders]);

  const loadSports = useCallback(async () => {
    const c = await fetch(`${API_BASE}/api/sports/services?region=${region}`, { headers: authHeaders() });
    if (c.ok) setCompetitions((await c.json()).competitions || []);
    if (!isAuthenticated) { setFollowed([]); return; }
    const f = await fetch(`${API_BASE}/api/sports/follows`, { headers: authHeaders() });
    if (f.ok) setFollowed((await f.json()).competitions || []);
  }, [region, isAuthenticated, authHeaders]);

  useEffect(() => { loadServices(); }, [loadServices]);
  useEffect(() => { loadSchedule(); }, [loadSchedule]);
  useEffect(() => { loadSubsAndAnalysis(); }, [loadSubsAndAnalysis]);
  useEffect(() => { loadSports(); }, [loadSports]);

  const toggleCompetition = async (comp) => {
    const next = followed.includes(comp) ? followed.filter((c) => c !== comp) : [...followed, comp];
    setFollowed(next);
    await fetch(`${API_BASE}/api/sports/follows`, {
      method: 'PUT', headers: authHeaders(), body: JSON.stringify({ competitions: next }),
    });
    loadSubsAndAnalysis();  // re-run analyse so sports subscriptions are re-judged
  };

  const subbed = new Set(subs.map((s) => s.provider_id));
  // Match by tmdb id alone: the schedule's media_type is resolved server-side and
  // may differ from the watchlist's guess, so key-by-type would miss matches.
  const scheduledIds = new Set(schedule.map((s) => String(s.tmdb_id)));

  const addToSchedule = async (item) => {
    const media_type = item.category === 'TV Series' ? 'tv' : 'movie';
    await fetch(`${API_BASE}/api/optimiser/schedule`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ tmdb_id: item.movieId, media_type, title: item.movieTitle }),
    });
    loadSchedule();
  };

  const removeFromSchedule = async (s) => {
    await fetch(`${API_BASE}/api/optimiser/schedule/${s.media_type}/${s.tmdb_id}`, {
      method: 'DELETE', headers: authHeaders(),
    });
    loadSchedule();
  };

  const toggleSub = async (svc) => {
    const on = subbed.has(svc.provider_id);
    await fetch(`${API_BASE}/api/optimiser/subscriptions${on ? '/' + svc.provider_id : ''}`, {
      method: on ? 'DELETE' : 'POST',
      headers: authHeaders(),
      body: on ? undefined : JSON.stringify({ provider_id: svc.provider_id, country }),
    });
    loadSubsAndAnalysis();
  };

  const runOptimise = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/optimiser/optimise?country=${country}`, {
        method: 'POST', headers: authHeaders(),
      });
      if (r.ok) setResult(await r.json());
    } finally { setBusy(false); }
  };

  const runPlan = async () => {
    setPlanBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/optimiser/plan?country=${country}`, {
        method: 'POST', headers: authHeaders(),
      });
      if (r.ok) setPlan(await r.json());
    } finally { setPlanBusy(false); }
  };

  const ccy = analysis?.currency || result?.currency || (country === 'US' ? 'USD' : 'GBP');

  return (
    <div className="min-h-screen bg-gray-50">
      <SiteHeader />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
              <Wallet className="w-6 h-6 text-videbits-600" /> Subscription Optimiser
            </h1>
            <p className="text-sm text-gray-500 mt-1">Manage your streaming portfolio: maximum content, minimum spend.</p>
          </div>
          <select value={country} onChange={(e) => setCountry(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white">
            {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
        </div>

        {!isAuthenticated ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-600">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-gray-400" />
            Sign in to track your subscriptions and optimise your watch schedule.
          </div>
        ) : (
          <>
            {/* Dashboard */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Stat label="Monthly spend" value={money(analysis?.current_spend, ccy)} />
              <Stat label="Schedule coverage" value={analysis ? `${analysis.coverage_percentage}%` : '—'} />
              <Stat label="Active services" value={analysis?.active_services?.length ?? subs.length} />
              <Stat label="Potential savings" value={money(analysis?.potential_savings, ccy)} accent />
            </div>

            {analysis?.redundant_services?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 text-sm text-amber-800 flex items-start gap-2">
                <TrendingDown className="w-4 h-4 mt-0.5" />
                <span>Consider pausing <strong>{analysis.redundant_services.map((s) => s.name).join(', ')}</strong> — its content is covered by your other services. Saves {money(analysis.potential_savings, ccy)}/mo.</span>
              </div>
            )}

            {analysis && analysis.availability_complete === false && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-8 text-xs text-gray-500 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5" />
                <span>Still resolving streaming availability for some titles — savings recommendations are paused until that's complete. Re-run shortly.</span>
              </div>
            )}

            {/* Watch schedule — what the optimiser is based on */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 mb-8">
              <h2 className="text-lg font-semibold text-gray-900">Watch schedule</h2>
              <p className="text-xs text-gray-500 mb-3">The optimiser is based on these titles — what you actually plan to watch.</p>
              {schedule.length > 0 ? (
                <div className="flex flex-wrap gap-2 mb-3">
                  {schedule.map((s) => (
                    <span key={`${s.media_type}:${s.tmdb_id}`} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm">
                      {s.title}
                      <button onClick={() => removeFromSchedule(s)} className="text-videbits-500 hover:text-red-600" aria-label="Remove">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 mb-3">Your schedule is empty. Add titles from your watchlist below.</p>
              )}

              {(() => {
                const candidates = watchlist.filter((w) => !scheduledIds.has(String(w.movieId)));
                if (candidates.length === 0) {
                  return <p className="text-xs text-gray-400">Add titles to your <Link to="/watchlist" className="text-videbits-600 hover:underline">watchlist</Link> to schedule them here.</p>;
                }
                return (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-2">Add from your watchlist</p>
                    <div className="flex flex-wrap gap-2">
                      {candidates.map((w) => (
                        <button key={w.id} onClick={() => addToSchedule(w)}
                                className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-videbits-200 text-videbits-700 text-sm hover:border-videbits-400 hover:bg-videbits-50">
                          <Plus className="w-3.5 h-3.5 text-videbits-500" /> {w.movieTitle}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Sports you follow — the optimiser judges sports subs against these */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 mb-8">
              <h2 className="text-lg font-semibold text-gray-900">Sports you follow</h2>
              <p className="text-xs text-gray-500 mb-3">Pick the competitions you watch — sports aren't in your watch schedule, so the optimiser uses these to judge whether a sports subscription is worth keeping.</p>
              <div className="flex flex-wrap gap-2">
                {competitions.map((comp) => {
                  const on = followed.includes(comp);
                  return (
                    <button key={comp} onClick={() => toggleCompetition(comp)}
                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-sm transition-colors ${on ? 'border-videbits-500 bg-videbits-50 text-videbits-800' : 'border-videbits-200 text-videbits-700 hover:border-videbits-400 hover:bg-videbits-50'}`}>
                      {on ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5 text-videbits-500" />} {comp}
                    </button>
                  );
                })}
              </div>
              {analysis?.uncovered_competitions?.length > 0 && (
                <p className="text-xs text-amber-700 mt-3 flex items-start gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5" />
                  You follow {analysis.uncovered_competitions.join(', ')} but none of your subscribed services carry {analysis.uncovered_competitions.length > 1 ? 'them' : 'it'}.
                </p>
              )}
            </div>

            {/* Service picker */}
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Your services</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
              {services.map((svc) => {
                const on = subbed.has(svc.provider_id);
                return (
                  <button key={svc.provider_id} onClick={() => toggleSub(svc)}
                          className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left transition-colors ${on ? 'border-videbits-500 bg-videbits-50' : 'border-videbits-200 bg-videbits-50 hover:border-videbits-400'}`}>
                    <div>
                      <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                        {svc.name}
                        {svc.kind === 'sports' && (
                          <span className="px-1.5 py-0.5 rounded bg-gray-200 text-gray-600 text-[10px] uppercase tracking-wide">Sports</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{money(svc.monthly_price, svc.currency)}/mo</div>
                    </div>
                    {on ? <Check className="w-5 h-5 text-videbits-600" /> : <Plus className="w-5 h-5 text-videbits-500" />}
                  </button>
                );
              })}
            </div>

            {/* Optimise */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Cheapest way to watch your schedule</h2>
                <button onClick={runOptimise} disabled={busy}
                        className="px-4 py-2 bg-videbits-600 text-white rounded-lg text-sm font-medium hover:bg-videbits-700 disabled:opacity-50">
                  {busy ? 'Optimising…' : 'Optimise'}
                </button>
              </div>
              {result ? (
                <div>
                  <div className="flex flex-wrap gap-4 mb-5">
                    <Stat label="Recommended cost" value={`${money(result.monthly_cost, ccy)}/mo`} accent />
                    <Stat label="Coverage" value={`${result.coverage_percentage}%`} />
                    <Stat label="Schedule" value={result.schedule_size} />
                  </div>

                  {/* Recommended services with their prices */}
                  <p className="text-xs font-medium text-gray-500 mb-2">Subscribe to</p>
                  <div className="space-y-1.5 mb-5">
                    {result.selected_services.map((s) => (
                      <div key={s.provider_id} className="flex items-center justify-between text-sm border-b border-gray-100 pb-1.5">
                        <span className="font-medium text-gray-900">{s.name}</span>
                        <span className="text-gray-500">{money(s.monthly_cost, ccy)}/mo</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between text-sm pt-1">
                      <span className="font-semibold text-gray-900">Total</span>
                      <span className="font-semibold text-gray-900">{money(result.monthly_cost, ccy)}/mo</span>
                    </div>
                  </div>

                  {/* Per-title breakdown */}
                  {result.breakdown?.length > 0 && (
                    <>
                      <p className="text-xs font-medium text-gray-500 mb-2">What you'll watch where</p>
                      <div className="space-y-1 mb-4">
                        {result.breakdown.map((b) => (
                          <div key={`${b.provider_id}:${b.title}`} className="flex items-center justify-between text-sm">
                            <span className="text-gray-700">{b.title}</span>
                            <span className="px-2 py-0.5 rounded-full bg-videbits-100 text-videbits-800 text-xs font-medium">{b.service}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {result.missing_titles?.length > 0 && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-600">
                      <span className="font-medium">Not on any subscription service:</span> {result.missing_titles.join(', ')}
                      <span className="text-gray-400"> — only available to rent/buy, or not streaming.</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">Add titles to your watch schedule above, then run the optimiser to see the cheapest service combination.</p>
              )}
            </div>

            {/* Release-aware plan */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 mt-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Release-aware plan</h2>
                  <p className="text-xs text-gray-500">Subscribe in sequence — only pay for a service the month you watch its title.</p>
                </div>
                <button onClick={runPlan} disabled={planBusy}
                        className="px-4 py-2 bg-videbits-600 text-white rounded-lg text-sm font-medium hover:bg-videbits-700 disabled:opacity-50">
                  {planBusy ? 'Planning…' : 'Plan'}
                </button>
              </div>
              {plan ? (
                <div>
                  {plan.savings > 0 ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-sm text-green-800">
                      Sequencing costs <strong>{money(plan.sequenced_cost, ccy)}</strong> across the run vs <strong>{money(plan.always_on_cost, ccy)}</strong> holding everything — <strong>saves {money(plan.savings, ccy)}</strong>.
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 mb-4">Your titles cluster in time, so sequencing doesn't beat subscribing together ({money(plan.sequenced_cost, ccy)}).</p>
                  )}
                  <div className="space-y-3">
                    {plan.calendar.map((m) => (
                      <div key={m.month} className="border-l-2 border-videbits-300 pl-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-900">{monthLabel(m.month)}</span>
                          <span className="text-sm text-gray-500">{money(m.monthly_cost, ccy)}</span>
                        </div>
                        <div className="text-xs text-gray-600">
                          <span className="text-gray-400">Subscribe:</span> {m.services.map((s) => s.name).join(', ')} · <span className="text-gray-400">Watch:</span> {m.titles.join(', ')}
                        </div>
                      </div>
                    ))}
                  </div>
                  {plan.uncovered_titles?.length > 0 && (
                    <p className="text-xs text-gray-400 mt-3">Not on any subscription service: {plan.uncovered_titles.join(', ')}</p>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">See when to subscribe to each service based on your schedule's release dates.</p>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-4 py-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`text-xl font-semibold ${accent ? 'text-green-600' : 'text-gray-900'}`}>{value}</div>
    </div>
  );
}
